import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiCheck, FiPlus, FiTrash } from 'react-icons/all';
import {
  FiChevronLeft,
  FiDownload,
  FiEdit2,
  FiEdit3,
  FiX,
} from 'react-icons/fi';
import { Input, Table } from '~/components';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { useSalesSegment } from '~/hooks/useSalesSegment';
import { SalesSegment } from '~/types/SalesSegment';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';

type FormData = {
  nm_tipo: string;
};

export const SalesSegmentForm: React.FC<{ externalUser: boolean }> = ({
  externalUser,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const btnRef = React.useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isEdit, setEdit] = useState<boolean[]>([]);
  const [segmento, setSegmento] = useState<string>('');
  const [segmentoEdit, setSegmentoEdit] = useState<SalesSegment[]>([]);
  const [isFetching, setFetching] = useState(false);
  const [idSegmento, setIdSegmento] = useState<number>(0);
  const toast = useToast();
  const {
    data: segments,
    isLoading: isLoadingSegments,
    refetch,
  } = useSalesSegment();

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearErrors();
    onClose();
  };

  const handleCreate = () => {
    clearErrors();

    (!segmento || !segmento?.trim().length) &&
      setError('segmento', {
        type: 'required',
        message: 'Escreva o nome do segmento',
      });
    segmento.length > 60 &&
      setError('segmento', {
        type: 'maxLength',
        message: 'Nome do segmento com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { nome: segmento?.trim() };

    setFetching(true);

    api
      .post(`segmento_venda`, form)
      .then(() => {
        toast({
          title: `Segmento adicionado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setSegmento('');
        setValue('segmento', '');
        queryClient.invalidateQueries('salesSegments');
        queryClient.invalidateQueries('salesSegmentsAsSelect');
      })
      .catch(error => {
        if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);
          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao criar segmento`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao adicionar segmento`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  };

  async function handleSubmitEdit(index: number) {
    clearErrors();

    (!segmentoEdit[index] || !segmentoEdit[index].nome.trim().length) &&
      setError(`segment-${index}`, {
        type: 'required',
        message: 'Escreva o nome do segmento',
      });

    segmentoEdit[index].nome.length > 60 &&
      setError(`segment-${index}`, {
        type: 'maxLength',
        message: 'Nome do segmento com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { nome: segmentoEdit[index].nome.trim(), ativo: true };

    setFetching(true);

    api
      .put(`segmento_venda/${segmentoEdit[index]?.id}`, form)
      .then(() => {
        toast({
          title: `Segmento editado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setSegmentoEdit([...segmentoEdit]);
        handleEdit(index);
        queryClient.invalidateQueries('salesSegments');
        queryClient.invalidateQueries('salesSegmentsAsSelect');
      })
      .catch(error => {
        toast({
          title: `Erro ao editar tipo`,
          description: `${
            segmentoEdit[index].nome.length > 60
              ? 'Nome do segmento com mais de 60 caracteres'
              : !segmentoEdit[index].nome.trim().length
              ? 'Nome do segmento não pode ser vazio'
              : 'Nome do segmento não pode ser repetido'
          }`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleEdit = (i: number, value?: SalesSegment) => {
    clearErrors();
    isEdit[i] = !isEdit[i];
    setEdit([...isEdit]);
    if (value) {
      segmentoEdit[i] = value;
      setSegmentoEdit([...segmentoEdit]);
    }
  };

  const canEdit = (index: number) => {
    return isEdit[index];
  };

  const handleTipo = (event: any) => {
    setSegmento(event.target.value);
    clearErrors();
  };

  const handleTipoEdit = (event: any, index: number, id: number) => {
    segmentoEdit[index] = { id, nome: event.target.value };
    clearErrors(`type-${index}`);
  };

  const confirmarExclusao = (id: number) => {
    setIdSegmento(id);
    onOpenModal();
  };
  async function handleDelete() {
    onCloseModal();
    if (!idSegmento) return;
    api
      .delete(`segmento_venda/${idSegmento}`)
      .then(() => {
        toast({
          title: `Segmento de venda deletado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('salesSegments');
        queryClient.invalidateQueries('salesSegmentsAsSelect');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `${error.response.data.detail}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        color="white"
        background="gray.900"
        _hover={{
          bgColor: 'black',
        }}
        style={{ marginTop: 'auto', marginLeft: '10px' }}
        fontSize={{ lg: 'sm', md: 'sm' }}
        w="70px"
      >
        Novo
      </Button>
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="md">
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>
            <Icon
              as={FiChevronLeft}
              style={{
                cursor: 'pointer',
                fontSize: '28px',
                marginRight: '10px',
              }}
              onClick={handleClose}
            />
            Cadastro de segmento de venda
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={400} w="100%">
              <Stack gridGap={4} />
              <FormControl>
                <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                  Novo segmento
                </FormLabel>
                <InputGroup>
                  <FormControl isInvalid={errors?.segmento}>
                    <Input
                      name="segmento"
                      type="text"
                      error={errors.segmento}
                      fontSize="15px"
                      placeholder="Entre com o novo segmento"
                      onChange={handleTipo}
                      value={segmento}
                    />
                  </FormControl>
                  <Button
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    style={{ marginLeft: '10px' }}
                    onClick={() => handleCreate()}
                  >
                    <Icon as={FiPlus} />
                  </Button>
                </InputGroup>
              </FormControl>
              <Table type="secondary" size="sm">
                <Thead>
                  <Tr>
                    <Th>Segmento</Th>
                    <Th style={{ textAlign: 'right', paddingRight: '45px' }}>
                      Ações
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {segments &&
                    !isLoadingSegments &&
                    segments?.map((t: any, index: number) => (
                      <Tr key={t.id}>
                        {!canEdit(index) ? (
                          <>
                            <Td>
                              <Tooltip label={t.nome} placement="top">
                                <Text isTruncated style={{ width: '240px' }}>
                                  {t.nome}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td
                              style={{
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Tooltip label="Editar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Edit type"
                                  icon={<Icon as={FiEdit2} />}
                                  onClick={() => handleEdit(index, t)}
                                  marginRight="5px"
                                />
                              </Tooltip>
                              <Tooltip label="Deletar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Remove type"
                                  icon={<Icon as={FiTrash} />}
                                  onClick={() => confirmarExclusao(t.id)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        ) : (
                          <>
                            <Td>
                              <FormControl isInvalid={errors[`type-${index}`]}>
                                <Input
                                  name="segment"
                                  type="text"
                                  error={errors[`segment-${index}`]}
                                  fontSize="15px"
                                  placeholder="Editar o segmento de vedas"
                                  size="sm"
                                  defaultValue={segmentoEdit[index].nome}
                                  onChange={e => handleTipoEdit(e, index, t.id)}
                                />
                              </FormControl>
                            </Td>
                            <Td style={{ textAlign: 'right' }}>
                              <Tooltip label="Confirmar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Confirm"
                                  icon={<Icon as={FiCheck} />}
                                  marginRight="5px"
                                  onClick={() => handleSubmitEdit(index)}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Cancel"
                                  icon={<Icon as={FiX} />}
                                  onClick={() => handleEdit(index)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </Stack>
            <Button
              onClick={handleClose}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px', marginBottom: '20px' }}
            >
              Voltar
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <ConfirmationDialog
        titulo="Excluir Segmento"
        onClose={onCloseModal}
        confirmar={() => handleDelete()}
        isOpen={isOpenModal}
      >
        <Text>Tem certeza que deseja excluir o segmento?</Text>
      </ConfirmationDialog>
    </>
  );
};
